<script setup>
import { ref, computed, watch } from 'vue';
import PolicyItemResult from "~/components/search/PolicyItemResult.vue";
import MeetingItemResult from "~/components/search/MeetingItemResult.vue";
const config = useRuntimeConfig();
const route = useRoute()
const router = useRouter();
const searchQuery = ref(route.query.query || '');
const currentQuery = ref(searchQuery || '');
const publicSide = true;

const now = new Date();
const currentYear = now.getFullYear();
const years = computed(() => {
  if (results.value?.metadata?.meeting_years) {
    // Only show years present in metadata, sorted descending (most recent first)
    return Object.keys(results.value.metadata.meeting_years)
      .map(Number)
      .sort((a, b) => b - a);
  }
  // Fallback to static range if no metadata
  return Array.from({ length: 6 }, (_, i) => currentYear - 3 + i);
});

const availableMonths = computed(() => {
  if (!results.value?.metadata?.meeting_years) {
    // Fallback: all months
    return months.map((label, idx) => ({
      value: idx + 1,
      label
    }));
  }
  if (!selectedYear.value) {
    // No year selected: show all 12 months
    return months.map((label, idx) => ({
      value: idx + 1,
      label
    }));
  }
  const yearMonths = results.value.metadata.meeting_years[selectedYear.value];
  if (!yearMonths) {
    return [];
  }
  return yearMonths.map(monthNum => ({
    value: monthNum,
    label: months[monthNum - 1]
  }));
});

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const resultType = ref(route.query.type || 'Any');
const selectedYear = ref(route.query.year || '');
const selectedMonth = ref(route.query.month || '');


// Keep filter state in sync with route query
watch(
  () => route.query,
  (newQuery) => {
    if (newQuery.year !== selectedYear.value) selectedYear.value = newQuery.year || '';
    if (newQuery.month !== selectedMonth.value) selectedMonth.value = newQuery.month || '';
    if (newQuery.query !== searchQuery.value) searchQuery.value = newQuery.query || '';
    if (newQuery.type !== resultType.value) resultType.value = newQuery.type || 'Any';
    }
  );

  // Watch for filter changes and update URL (excluding resultType, which has its own watcher)
  watch([selectedYear, selectedMonth, searchQuery], ([newYear, newMonth, newQuery]) => {
    const query = { ...route.query };
    if (newYear) {
      query.year = newYear;
    } else {
      delete query.year;
      // Clear month when year is cleared
      delete query.month;
      selectedMonth.value = '';
    }
    if (newMonth) {
      query.month = newMonth;
    } else {
      delete query.month;
    }
    if (newQuery) {
      query.query = newQuery;
    } else {
      delete query.query;
    }
    if (resultType.value && resultType.value !== 'Any') {
      query.type = resultType.value;
    } else {
      delete query.type;
    }
    router.replace({ query });
  });

// Watch for resultType changes and update URL
watch(resultType, (newType) => {
  const query = { ...route.query };
  if (newType && newType !== 'Any') {
    query.type = newType;
  } else {
    delete query.type;
  }
  router.replace({ query });
});

// Handle search submission
const handleSearch = (event) => {
  console.log('handleSearch', event.key);
  if (event.key !== 'Enter') return;
  searchQuery.value = currentQuery.value;

  const query = { ...route.query };
  if (searchQuery.value) {
    query.query = searchQuery.value;
  } else {
    delete query.query;
  }
  delete query.month;
  delete query.year;

  router.push({ query }).then(() => {
    refresh();
  });

};

definePageMeta({
  prefetchLinks: false
});

// Reactive async data fetching
const { data: results, pending, error, refresh } = await useAsyncData(
  'public-search-results',
  () => {
    if (!searchQuery.value) {
      return Promise.resolve(null);
    }
    
    return publicFetch(`/public-sites/search`, {
      method: 'GET',
      query: {
        query: searchQuery.value,
        year: selectedYear.value,
        month: selectedMonth.value,
        type: resultType.value !== 'Any' ? resultType.value : undefined
      }
    });
  },
  {
    watch: [selectedYear, selectedMonth, resultType]
  }
);
</script>

<template>
  <h1 class="text-2xl mb-6">Search results</h1>
  <div class="relative w-full sm:w-7/12">
    <label for="search-input" class="sr-only">Search meetings and policies</label>
    <font-awesome :icon="['fas', 'search']" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
    <input
      id="search-input"
      type="search"
      v-model="currentQuery"
      @keyup.enter="handleSearch"
      class="w-full p-2 pl-10 border border-gray-300 rounded-md"
      placeholder="Search..."
    />
  </div>
  <div class="flex flex-col sm:flex-row sm:items-start gap-6 w-full pt-8">
    <div class="filter-container w-full sm:w-1/2 md:w-1/4 mb-4 sm:mb-0">
      <div class="bg-white  border-gray-200 rounded-lg sm:p-4 sm:shadow-sm">
        <div class="justify-center text-gray-700 text-xs font-medium font-['Söhne'] uppercase leading-none tracking-tight mb-4">Filter results</div>
        <!-- Result Type -->
        <label class="block text-sky-950 font-medium mb-1 text-sm" for="resultType">Result type</label>
        <select id="resultType" v-model="resultType" class="w-full mb-5 px-4 py-3 border border-blue-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-sky-950">
          <option value="Any">Any</option>
          <option value="meetings">Meetings</option>
          <option value="policies">Policies</option>
          <option value="documents">Documents</option>
        </select>
        <!-- Year -->
        <div class="hidden sm:block">
            <label class="block text-sky-950 font-medium mb-1 text-sm" for="year">Year</label>
            <select id="year" v-model="selectedYear"
                    class="w-full mb-5 px-4 py-3 border border-blue-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-sky-950">
              <option value="">Select...</option>
              <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
            </select>
        </div>
        <!-- Month -->
          <div class="hidden sm:block">
            <label class="block text-sky-950 font-medium mb-1 text-sm" for="month">Month</label>
            <div class="relative">
              <select
                id="month"
                v-model="selectedMonth"
                :disabled="!selectedYear"
                class="w-full mb-2 px-4 py-3 border border-blue-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white text-sky-950 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <option value="">Select...</option>
                <option v-for="month in availableMonths" :key="month.value" :value="month.value">{{ month.label }}</option>
              </select>
              <div v-if="!selectedYear" class="absolute inset-0 bg-transparent cursor-help" title="Please select a year first"></div>
            </div>
        </div>
      </div>
    </div>
    <div class="main-container w-full sm:w-1/2 md:w-3/4 sm:border-l border-gray-300 sm:pl-10">
      <template v-if="pending">
        <div class="py-8 text-center text-gray-400">Loading results...</div>
      </template>
      <template v-else-if="error">
        <div class="py-8 text-center text-red-500">Error loading results.</div>
      </template>
      <template v-else>
        <template v-if="results">
          <template v-if="results.policy_items && results.policy_items.length > 0">
            <PolicyItemResult v-for="policy in results.policy_items" :key="policy.id" :policy="policy" :public-side="true">
            </PolicyItemResult>
          </template>
          <template v-if="results.meetings && results.meetings.length > 0">
            <MeetingItemResult
              v-for="(meeting, index) in results.meetings"
              :key="index"
              :meeting="meeting"
            />
          </template>
          <div v-if="!results.policy_items?.length && !results.meetings?.length" class="py-8 text-center text-gray-400">
            No results found.
          </div>
        </template>
        <div v-else class="py-8 text-center text-gray-400">
          Enter a search query
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}
</style>